/**
 * What the card says to *this* reader, and the one button it gives them.
 *
 * A module is set by a teacher and played by a class, and the same card is read by
 * both. The block under the playlist is where the two part company:
 *
 *  - a **learner** is shown their classmates — the faces, a sentence sized to how
 *    many have played, and the record when there is one to take — and is offered
 *    the challenge;
 *  - **everybody else** (a teacher, a parent, the maker, or a reader the app cannot
 *    place) is offered the way in and nothing else.
 *
 * The decision itself is pure and lives in `#/lib/moduleAudience`; this is the
 * drawing of it, written once so the card in a feed and the module's own page
 * cannot say two different things about the same class.
 *
 * ## The sentence, and who it is allowed to name
 *
 * The row of faces says *how many*; the sentence says *who*, and it only starts
 * naming people once there are enough of them for a count to be dull. Four rungs,
 * richest first (see `ClassSentenceText`):
 *
 *  - **A hundred finishers**: *ten prize winners, led by X in first place* — the
 *    board's own gate (`MODULE_LEADERBOARD_MIN_FINISHERS`), and the one line on
 *    the card that says outright that there is something to win.
 *  - **Ninety players**: *A, B and C are in the lead* — the lower, play-counted
 *    gate (`MODULE_LEADERBOARD_LEADERS_PLAYS`), so the card names the front of the
 *    class while the podium itself is still locked.
 *  - **A friend**: when somebody the reader follows has played, the sentence starts
 *    with *their* name, whatever rung it lands on. A name the reader chose is worth
 *    more than any count, and it is the only thing on this block that is about them
 *    rather than about the class.
 *  - **Otherwise**: how many have played, and whether the top places are still open.
 *
 * ## The test mode
 *
 * A dev chip on the card — *Show all cases* — stops drawing the one case that
 * happens to be true and draws **every case of the reel instead**, in the order a
 * reader meets them, each row read off a board invented for it
 * (`moduleCaseFacts` in `#/lib/moduleFixtures`) and each row printing the whole of
 * that case's copy: the sentence and its punch, the line about the run, the
 * reader's own row, the countdown to the top three, and the word on the button.
 *
 * The first build of the sheet printed the *rungs* of one sentence, all read off the
 * one board the reader happened to have — and one board can only ever fill one row,
 * so six of the seven printed what they were waiting for (*needs a board of a
 * hundred finishers*) instead of the copy they existed to show. Inventing the board
 * per case is what makes every row say its real sentence, and it is also what lets
 * the rows be the reel's own cases rather than a second list of rungs kept here and
 * read only when the board happened to fit.
 *
 * It is a second reading of the same store the reel at the top of a post's header
 * walks (`ModuleCaseSwitcher`): the reel shows one case *on the card*, the sheet
 * shows all of them *beside* it. Asking for the sheet also asks for the three facts
 * the card itself is read from — the biggest board, a student's seat, a fixture
 * friend — because a sheet drawn over an empty card would say nothing about the
 * case it stands on (see `useModuleAudience`). Passed only by an `IS_DEV` build, so
 * a production card has no path to a spec sheet where a post should be.
 *
 * ## Cases still to build
 *
 * The block reads one sentence out of a few numbers, and there are more readings
 * waiting behind it. They are deliberately not built yet, and they are written down
 * here rather than discovered one at a time:
 *
 *  - **A friend who is not on the podium.** They played, they are somewhere in the
 *    class, and below both gates the sentence says nothing about them.
 *  - **Several friends at once** — who is named when two people the reader follows
 *    both played? Today it is simply the best-placed one.
 *  - **A friend ahead of the reader** — the sentence should say *you are behind
 *    them*, not only *they are here*.
 *  - **The reader's own name** among the leaders or the prize winners.
 *  - **A tie for a place** — two learners on the same ratio share a rank today, and
 *    a sentence that names *the three in the lead* can name two people for 2nd.
 *  - **A teacher's own class** — the leaders are the module's everywhere, not the
 *    room's.
 *  - **Classroom- and school-scoped boards**, so *the class* means one room and the
 *    sentence can compare the reader with the people beside them.
 *  - **A friend who is behind the reader** — the sentence could congratulate rather
 *    than challenge.
 *  - **Right-to-left reading order** for the (OOOO(+X row and the sentence beside it.
 *  - **A board smaller than the faces drawn** — where the (+N) bubble never appears.
 *
 * ## Four more things worth knowing before editing
 *
 *  - **A run under way changes the sentence and the word, not the action.** The
 *    button is the same button and does the same thing — it opens the player,
 *    which continues where the learner stopped (`#/lib/moduleRun`). And how close
 *    the run is decides the *tone*: three capsules in, or halfway, the button goes
 *    hot — a 🔥, a gradient, the one place the card raises its voice — because a
 *    run that close is one sitting from a rank. Below that it keeps the quiet blue
 *    and simply says what is left, and a run that is *over* says the one thing left
 *    to say about a finished course: *play again* — where the first build handed
 *    the reader the word for a challenge they had already answered
 *    (`modulePlayWord`).
 *  - **The top three are the board's, not the card's.** Nothing here draws a
 *    podium any more: the class's own faces *are* the way to the results, and the
 *    results screen does the podium properly, so a smaller copy of the same three
 *    names on the card was the same fact twice in a place with no room for it. What
 *    the block still says is how far the class is from unlocking it — the honest
 *    version of the same idea, in one quiet line.
 *  - **The faces are the door.** A row of faces is what a reader presses when they
 *    want to know who those people are, so when there is a board to open the row
 *    opens it (`ModulePeerStack`'s `onPress`), instead of the card drawing a second
 *    row of the same faces with the word *Leaderboard* beside them.
 *  - **Everybody gets the row, not only a learner.** The block used to be drawn for
 *    a peer alone, and every other reader was handed a door — a glyph, the word
 *    *Leaderboard* and a chevron — where the class should have been. A teacher is
 *    the reader with the least to compare and the most to *see*: the faces at the
 *    top of the board are the evidence that the work is being done, and a name they
 *    follow among them is the loudest version of that fact. So the same row is
 *    drawn for them, from the board's own leaders rather than from a year
 *    (`moduleAudienceFaces`), and the only thing that changes is the tone of the
 *    copy: the challenge punch is a competitor's, and a teacher is not competing.
 *  - **A learner's own row is repeated back to them**, because a competition you
 *    cannot find yourself in is a list of strangers.
 *  - **The first player is shown themselves.** With nobody on the board there are
 *    no classmates and no record — and the reader who most needs telling that the
 *    first result is theirs to set is the reader whose face would otherwise be
 *    missing from the row, so the row draws them, alone, and the sentence is read
 *    *in front of that face* rather than under it, because the two are one fact:
 *    the person who is here first.
 *  - **A reader the class has nothing to say to is still told the count.** An
 *    unplayed module draws no faces for anybody, and the sentence that is left is
 *    the social fact itself — *no one has played this module yet* — rather than an
 *    invitation to be the first player, which is only true of a learner (see
 *    `nobodyYet`).
 *  - **Nothing on the card promises a place.** A lead is not a title — the board's
 *    whole point is that a better score takes the top — so every sentence says what
 *    playing *buys* (a score to beat, a rank, a place on the board) and never what
 *    it *wins*. The one that did has been rewritten, and the rungs that were close
 *    to it say the same thing more carefully now (see the class block, and
 *    `progressSentence`).
 *  - **Every new string goes through `<Trans>` or `<Plural>`** — whole sentences
 *    rather than assembled fragments, and ternaries rather than `&&`, because a
 *    condition that renders `false` straight into a `<Text>` prints the word.
 */
import {Pressable, StyleSheet, View} from 'react-native'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  moduleAudienceFaces,
  modulePlayWord,
  MODULE_LEADERS_NAMED,
  type ModuleAudience as ModuleAudienceFacts,
  type ModuleCaseFacts,
} from '#/lib/moduleAudience'
import {
  MODULE_PREVIEW_CASES,
  moduleCaseFacts,
  type ModulePreviewCase,
} from '#/lib/moduleFixtures'
import {moduleRunIsClose, type ModuleRunProgress} from '#/lib/moduleRun'
import {MODULE_LEADERBOARD_MIN_FINISHERS} from '#/lib/modulePlaylist'
import {formatRate, type ModulePlayerScore} from '#/lib/moduleLeaderboard'
import {atoms as a, tokens, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {GradientFill} from '#/components/GradientFill'
import {Play_Filled_Corner0_Rounded as Play} from '#/components/icons/Play'
import {Text} from '#/components/Typography'
import {ModulePeerStack} from './ModulePeerStack'

/** A name to print, from whatever a row carries. */
function nameOf(peer: ModulePlayerScore): string {
  return peer.name?.trim() || (peer.handle ? `@${peer.handle}` : '')
}

/**
 * Who played, as stakes — the one sentence the class block leads with.
 *
 * Chosen by the count, never assembled from names: the sizes of class read
 * nothing alike, and the *friend* rung is the one exception, because a name the
 * reader chose outranks any number the board can print. Everything it needs has
 * already been decided in `#/lib/moduleAudience` — the gates are there, not here,
 * so the card in a feed and the module's own page cannot choose different rungs —
 * and this function is only the wording. The list of readings still to build is in
 * the header.
 *
 * It takes the *facts* rather than a whole audience because the dev sheet draws it
 * thirteen times, once per case, off the facts each case invents
 * (`moduleCaseFacts`): the sentence is the same function in the feed and on the
 * sheet, which is the only way a sheet can be trusted as a spec for the card.
 */
function ClassSentenceText({facts}: {facts: ModuleCaseFacts}) {
  const t = useTheme()
  const ink = [a.text_sm, t.atoms.text]
  const punch = [a.text_sm, a.font_semi_bold, {color: t.palette.primary_500}]
  const friend = facts.friend
  const prizes = facts.prizeWinners
  const leaders = facts.leaders
  /** Whether the friend is one of the ten — the only place it changes this rung. */
  const friendWon =
    !!friend && prizes.some(prize => prize.did === friend.did) ? friend : null

  /**
   * A hundred finishers, and what is on the line.
   *
   * The fact is for everybody — *ten prize winners, led by X* is the class's news
   * and a teacher reading their own module is the reader it is news to. The second
   * line is the card's one attempt to raise a voice, and it is addressed at
   * somebody who can still take a place: a teacher has no place to take, so the
   * punch is a competitor's and only a competitor is given it. It names the thing
   * itself — *you can be one of the ten* — which is the shortest way to say it and
   * the widest: a place among the winners, rather than the first draft's route to
   * one (*you can break the record and win, too*).
   */
  const prize = () => {
    if (prizes.length === 0) return null
    const lead = nameOf(prizes[0])
    return (
      <>
        <Text style={ink}>
          {friendWon ? (
            friendWon.did === prizes[0].did ? (
              <Trans>
                {lead} is in first place, one of the ten prize winners.
              </Trans>
            ) : (
              <Trans>
                {nameOf(friendWon)} is one of the ten prize winners, led by{' '}
                {lead} in first place.
              </Trans>
            )
          ) : (
            <Trans>Ten prize winners, led by {lead} in first place.</Trans>
          )}
        </Text>
        {facts.isPeer ? (
          <Text style={punch}>
            <Trans>You can be one of the ten.</Trans>
          </Text>
        ) : null}
      </>
    )
  }

  /**
   * Ninety players: the three in front, and where the friend stands among them.
   *
   * The plain reading is the shortest sentence on the board that names anybody:
   * *A, B and C lead — 96 players*, with the verb doing the work the first build
   * spent five words on (*are in the lead*, *so far*). Which of the three is the
   * friend is decided by a did rather than by a mode argument, so the two readings
   * cannot be pointed at the wrong board.
   */
  const leading = () => {
    if (leaders.length < MODULE_LEADERS_NAMED) return null
    const [first, second, third] = leaders
    return (
      <Text style={ink}>
        {friend?.did === first.did ? (
          <Trans>
            {nameOf(friend)} is in the lead, ahead of {nameOf(second)} and{' '}
            {nameOf(third)}.
          </Trans>
        ) : friend ? (
          <Trans>
            {nameOf(friend)} is chasing {nameOf(first)} — {facts.played}{' '}
            players.
          </Trans>
        ) : (
          <Trans>
            {nameOf(first)}, {nameOf(second)} and {nameOf(third)} lead —{' '}
            {facts.played} players.
          </Trans>
        )}
      </Text>
    )
  }

  /**
   * A face the reader knows, below both gates: the name is the whole sentence.
   *
   * *— join them* was the card's own version of an invitation, and it is one the
   * reader already has: the button under the sentence says *challenge*, and an
   * invitation beside it was the same offer twice, in the one case where the only
   * news is the name.
   */
  const knownFace = friend ? (
    <Text style={ink}>
      <Trans>{nameOf(friend)} has played this module.</Trans>
    </Text>
  ) : null

  /*
   * The reader who is *first*.
   *
   * A learner of the module's own level with nobody on the board is the one
   * reader the whole block has nothing to say to — no classmates to sit behind,
   * no record to chase — and they are also the one reader whose face would be
   * missing from the row, because the row is built from plays and they have not
   * played. So the row draws *them* (see the block below).
   *
   * **And what it says is encouragement, and nothing else.** Three drafts of this
   * sentence tried to describe the situation — *nobody is ahead of you*, *your
   * result sets the record*, *set the score everyone else has to beat* — and every
   * one of them was a claim about a competition that has not happened yet: the
   * first result is only ahead until somebody posts a better one, and calling it
   * *the record* hands a learner a title they may hold for a day. On the one card
   * where nothing has gone wrong yet, that is the worst place to be caught
   * overselling. So the line is about the reader rather than about the board, it
   * is true on every board there will ever be, and the competitive half is left
   * where it belongs: the button below, which says *be the first player* and means
   * it. Everybody else gets the plain fact, because a teacher reading an unplayed
   * module is not the first player of anything and is owed the news, not a
   * cheer.
   */
  const nobodyYet = facts.isPeer ? (
    <Text style={punch}>
      <Trans>You're up first — give it your best!</Trans>
    </Text>
  ) : (
    <Text style={ink}>
      <Trans>No one has played this module yet.</Trans>
    </Text>
  )

  /**
   * Nobody yet, or how many played — the two states that need no names.
   *
   * One line for every reader, which is the change. The learner's copy used to add
   * *the top places are still open* and the teacher's did not, so two people looking
   * at the same number were told two different facts about it. The sentence is about
   * the class; what the reader stands to gain from it is the punch above and the
   * button below, which is where an offer belongs and where it can differ by reader
   * without the count changing. A learner is not left with less: *12 players so far*
   * and then *challenge* is the same fact the old line carried, in four words instead
   * of eleven.
   */
  const count =
    facts.played > 0 ? (
      <Text style={ink}>
        <Plural
          value={facts.played}
          one="One player so far."
          other="# players so far."
        />
      </Text>
    ) : (
      nobodyYet
    )

  /* The one case that is true for this reader, richest first. */
  return <>{prize() ?? leading() ?? knownFace ?? count}</>
}

/**
 * How far the class is from unlocking the top three.
 *
 * The card used to draw those three itself once they existed. It does not any more
 * (see the header), so the only thing left to say is what is still missing — and
 * only while something is missing: on a board past the gate the results screen holds
 * the podium, and a card that kept counting there would be counting backwards.
 *
 * `finishers <` the gate, and not `<=`. The board cuts to a podium *at* a hundred
 * finishers (`mode` in `#/lib/moduleLeaderboard` compares with `>=`), so a hundred
 * finishers would be a card telling the reader to wait for something the results
 * screen is already showing — the same one-finisher error the prize rung had, and
 * the reason both now read the same comparison.
 *
 * Takes the facts rather than the audience because the sheet prints one of these per
 * case, off each case's invented board.
 */
function ClassCountdown({facts}: {facts: ModuleCaseFacts}) {
  const t = useTheme()
  if (
    facts.played === 0 ||
    facts.finishers >= MODULE_LEADERBOARD_MIN_FINISHERS
  ) {
    return null
  }
  return (
    <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
      <Plural
        value={MODULE_LEADERBOARD_MIN_FINISHERS - facts.finishers}
        one="One more finisher and the top three appear."
        other="# more finishers and the top three appear."
      />
    </Text>
  )
}

/**
 * The reader's own row, in the two wordings it has.
 *
 * A competition you cannot find yourself in is a list of strangers, so the card
 * repeats the reader back to them: ranked, the place and the rate it was earned at;
 * unranked, how far they got and what finishing buys. The second wording is the
 * card's answer to a learner three capsules in, and it is where the promise that a
 * close run makes actually lives — *finish the module to be ranked* — which is why
 * the run line above it could afford to get short.
 *
 * Centred on the card, where it sits under the button as a footnote to the reader's
 * own action, and left-aligned in the sheet's column, where it is one more line of
 * the copy being reviewed.
 */
function OwnResult({
  viewer,
  testID,
  centered,
}: {
  viewer: ModulePlayerScore
  testID?: string
  centered?: boolean
}) {
  const t = useTheme()
  return (
    <Text
      testID={testID}
      style={[
        a.text_xs,
        centered ? a.text_center : null,
        t.atoms.text_contrast_medium,
      ]}>
      {viewer.rank ? (
        <Trans>
          You are #{viewer.rank} with {formatRate(viewer.pointsPerMinute)}{' '}
          pt/min.
        </Trans>
      ) : (
        <Trans>
          You have played {viewer.capsulesDone} of {viewer.capsuleCount}{' '}
          capsules. Finish the module to be ranked.
        </Trans>
      )}
    </Text>
  )
}

/**
 * What a case's invented board is, in one grey line under its name.
 *
 * It is the replacement for the old `needs a board of a hundred finishers`: every
 * row now *has* its board, so what is worth printing is not what a case lacks but
 * what it is being read against — how many players, who the reader is pretending to
 * be, the name they follow, and how far their run has got.
 *
 * Assembled from translated *fragments* on purpose, against the whole-sentences rule
 * the app's own copy follows: it is dev furniture describing data (a count, a role, a
 * name, a progress), never seen by a reader of the shipped app, and thirteen
 * hand-written sentences would be thirteen things to keep in step with the case
 * table. The separator is the one a spec sheet uses.
 */
function CaseBoard({
  one,
  facts,
  progress,
}: {
  one: ModulePreviewCase
  facts: ModuleCaseFacts
  progress: ModuleRunProgress | null
}) {
  const t = useTheme()
  return (
    <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
      {facts.played === 0 ? (
        <Trans>nobody has played</Trans>
      ) : (
        <Trans>{facts.played} players</Trans>
      )}
      {' · '}
      {one.role === 'teacher' ? (
        <Trans>a teacher</Trans>
      ) : (
        <Trans>a learner</Trans>
      )}
      {facts.friend ? (
        <>
          {' · '}
          <Trans>you follow {nameOf(facts.friend)}</Trans>
        </>
      ) : null}
      {progress ? (
        <>
          {' · '}
          <Trans>
            {progress.done} of {progress.total} capsules done
          </Trans>
        </>
      ) : null}
    </Text>
  )
}

/**
 * One row of the sheet: one case of the reel, and every word the card says for it.
 *
 * The heading is the reel's own label (`MODULE_PREVIEW_CASES`), so the sheet and the
 * switcher at the top of a post cannot call the same case two things. Then the grey
 * line saying what the row is read against, and then the copy in the order the card
 * reads it: the sentence and its punch, the run line, the reader's own row, the
 * countdown to the top three, and the word on the button — the button's word last
 * because it is the thing a reader of the sheet is looking for.
 *
 * Dev furniture, like the chip above it: the words are the app's own and translated
 * like any copy, and the shape is a spec sheet's.
 */
function CaseRow({one, testID}: {one: ModulePreviewCase; testID?: string}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const {facts, progress} = moduleCaseFacts(one)
  return (
    <View
      testID={testID ? `${testID}-case-${one.id}` : undefined}
      style={[a.gap_xs, styles.row, t.atoms.border_contrast_low]}>
      <Text style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
        {l(one.label)}
      </Text>
      <CaseBoard one={one} facts={facts} progress={progress} />
      <ClassSentenceText facts={facts} />
      {progress ? (
        <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {progressSentence(progress)}
        </Text>
      ) : null}
      {facts.isPeer && facts.viewer ? (
        <OwnResult viewer={facts.viewer} />
      ) : null}
      <ClassCountdown facts={facts} />
      <Text style={styles.previewInk}>
        {l`BUTTON`} — {l(modulePlayWord({cta: facts.cta, progress}))}
      </Text>
    </View>
  )
}

/**
 * The sheet: every case of the reel, in order, each on its own board.
 *
 * The cases come from the table the reel walks rather than from a list kept here, so
 * a case added to the reel appears in the sheet the day it is added — and a case the
 * reel has stopped offering cannot go on being printed.
 *
 * The last row is the one line the card draws *outside* the sentence, and it is not a
 * case of the reel: the two wordings are read off the two run cases that earn them —
 * a run that is finished ranks, a run that is under way does not — so the sheet shows
 * both without inventing a fourteenth case.
 */
function CaseDeck({testID}: {testID?: string}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const ownRow = (id: string) => {
    const one = MODULE_PREVIEW_CASES.find(found => found.id === id)
    return one ? moduleCaseFacts(one).facts.viewer : null
  }
  const ranked = ownRow('run-done')
  const partWay = ownRow('run-three')
  return (
    <View
      testID={testID ? `${testID}-deck` : undefined}
      style={[a.gap_md, a.pt_sm]}>
      {MODULE_PREVIEW_CASES.map(one => (
        <CaseRow key={one.id} one={one} testID={testID} />
      ))}
      <View style={[a.gap_xs, styles.row, t.atoms.border_contrast_low]}>
        <Text style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
          {l`Your own result`}
        </Text>
        {ranked ? <OwnResult viewer={ranked} /> : null}
        {partWay ? <OwnResult viewer={partWay} /> : null}
      </View>
    </View>
  )
}

/**
 * The run, in the reader's own ear.
 *
 * What is left, and nothing else: the count while there is room to drift, and the
 * fire once the run is close (`moduleRunIsClose` — three capsules or halfway). A
 * finished run gets the plain fact, because fire over nothing is noise.
 *
 * **Short on purpose, and the promise moved rather than lost.** This was the one
 * long sentence on the card — *you have finished three of six capsules, complete the
 * rest* repeated a number the tiles above it already show and then told the reader to
 * do the thing the button beside it says. What a close run *buys* is still said, twice
 * and in the two places a reader looks: the button says *finish it*, and the reader's
 * own row under it says *finish the module to be ranked* (`#/lib/moduleLeaderboard`
 * is what ranks it, and nothing here promises a place). *1st place is in reach* was
 * the earlier draft of that promise and it was wrong — a rank is earned against
 * whoever else played, and the case where the sentence was plainly false was the
 * reader already in first place. A module is never a single capsule
 * (`MODULE_MIN_CAPSULES`), so *1 of 6 capsules* is always the plural form.
 */
function progressSentence(progress: ModuleRunProgress) {
  if (progress.finished) {
    return <Trans>Every capsule finished.</Trans>
  }
  if (moduleRunIsClose(progress)) {
    return (
      <Plural
        value={progress.total - progress.done}
        one="# capsule left 🔥"
        other="# capsules left 🔥"
      />
    )
  }
  return (
    <Trans>
      {progress.done} of {progress.total} capsules done.
    </Trans>
  )
}

export function ModuleAudience({
  audience,
  onPlay,
  onOpenBoard,
  title,
  viewerFace,
  progress,
  surfaceColor,
  allCases,
  onToggleCases,
  testID,
}: {
  audience: ModuleAudienceFacts
  /** The one action the block offers. */
  onPlay: () => void
  /**
   * What pressing the class's faces means — the way to the results.
   *
   * Handed down rather than navigated here: which module, and which route, belongs
   * to the surface that owns the screen.
   */
  onOpenBoard?: () => void
  /** The module's own name, for the sentence a screen reader reads. */
  title?: string
  /**
   * The reader's own face, for the one case that is about them alone.
   *
   * A reader who is the first to find a module is not on its board — the board is
   * plays — so the strip has nothing to draw from them unless the surface that
   * knows the session hands it over (see `firstPlayer`).
   */
  viewerFace?: {did: string; avatar?: string}
  /**
   * The colour of the surface this block is drawn on; the faces ring in it.
   *
   * A prop rather than a constant because the two callers sit on different
   * surfaces: the module's own page is the page, and the card in a feed is the
   * tinted band (`ModuleFeedEmbed`). A ring cut from the wrong one outlines every
   * face in a colour nothing else on the block uses.
   */
  surfaceColor?: string
  /**
   * How far *this reader* has got, on this device, when the caller knows.
   *
   * A run that is under way changes one word and adds one line. The button is the
   * same button and does the same thing — it opens the player, which continues
   * where the learner stopped (`#/lib/moduleRun`) — so *continue* is not a second
   * action dressed up as one; it is the same action, named for what it will do. A
   * learner who has finished every capsule is *not* continuing: the run starts again
   * from the top, and the button says so — *play again* (`modulePlayWord`).
   */
  progress?: ModuleRunProgress | null
  /**
   * Dev-only: draw every case of the reel instead of the one that is true.
   *
   * The answer to `onToggleCases`, so the chip and the copy cannot disagree about
   * whether the card is in test mode.
   */
  allCases?: boolean
  /**
   * Dev-only: what the *Show all cases* chip presses.
   *
   * Absent on surfaces with no chip of their own (the module's own page), where
   * the mode still *applies* — it is only the switch that is missing.
   */
  onToggleCases?: () => void
  testID?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()

  /*
   * The strip, and the number beside it.
   *
   * `moduleAudienceFaces` decides *who* — the reader's own result, then the one
   * they follow, then the class — and what is left here is the count the strip is
   * cut against, which has to come from the same list the faces do: a learner's
   * strip is their year, so *+8* means eight classmates, and every other reader's
   * is the board itself, so *+133* means a hundred and thirty-three players. One
   * number taken from the other list is the one lie this pair can tell.
   */
  const faces = moduleAudienceFaces(audience)
  const firstPlayer = audience.isPeer && audience.played === 0
  const shownFaces: readonly {did: string; avatar?: string}[] =
    firstPlayer && viewerFace ? [viewerFace] : faces
  const pool: readonly {did: string}[] = audience.isPeer
    ? audience.peers
    : audience.playerFaces
  const inPool = new Set(pool.map(face => face.did))
  const drawn = shownFaces.filter(face => inPool.has(face.did)).length
  const hiddenFaces = Math.max(
    0,
    (audience.isPeer ? audience.peers.length : audience.played) - drawn,
  )
  /*
   * The faces open the board.
   *
   * Absent when there is nothing behind them — a board of zero is not a screen,
   * and a pressable row that led to an empty list would be furniture pretending to
   * be a door.
   */
  const openBoard = audience.played > 0 ? onOpenBoard : undefined
  const facesLabel = openBoard
    ? title
      ? l`Results and leaderboard for ${title}`
      : l`Results and leaderboard`
    : undefined
  /* Fire only while there is something left to race for (`moduleRunIsClose`). */
  const hot = !!progress && moduleRunIsClose(progress)

  /*
   * The one word, chosen in the lib and translated here.
   *
   * The *choice* — a finished run, a run under way, or the word the audience already
   * decided — is a rule about the reader, so it lives beside that decision
   * (`modulePlayWord`); this is only where it is said in the reader's own language,
   * through the same `l` as everything else on the block.
   */
  const label = l(modulePlayWord({cta: audience.cta, progress}))

  const casesLabel = allCases ? l`HIDE ALL CASES` : l`SHOW ALL CASES`

  return (
    <View testID={testID} style={[a.gap_sm]}>
      {/*
        The dev door, above everything: every case at once.

        It sits *above* the class block because of the one reader who most needs it
        — a maker's own card has no class half at all, so a chip drawn inside the
        block would be a button that only appears once the block does. It is dashed
        and red, the preview bar's own signal, so it cannot be mistaken for app
        furniture; `onToggleCases` is passed only by an `IS_DEV` build (see
        `ModuleFeedEmbed`), so a production card never draws one.
      */}
      {onToggleCases ? (
        <Pressable
          testID={testID ? `${testID}-cases` : undefined}
          accessibilityRole="button"
          accessibilityState={{selected: !!allCases}}
          accessibilityLabel={casesLabel}
          onPress={onToggleCases}
          style={({pressed}) => [styles.cases, pressed && styles.casesPressed]}>
          <Text style={styles.previewInk}>{casesLabel}</Text>
        </Pressable>
      ) : null}

      {
        /*
          The class, for every reader.

          A learner's peers and a teacher's class are the same list read two ways
          — one is a year, the other is the board — and the block that shows it is
          the same block, so *who played this* cannot be drawn two ways on one
          card. There is no `isPeer` around it any more: the reader who is not a
          peer is the reader with the most to look at and the least to compare, and
          a card that answered them with a row saying *Leaderboard* was hiding the
          class behind the name of a screen.
        */
        <View
          testID={testID ? `${testID}-class` : undefined}
          style={[a.gap_sm]}>
          {/*
            The faces and the sentence, side by side while there is room for both.

            The card in a feed is a third narrower than the module's own page, and
            the same row in that width squeezed the sentence into three narrow
            lines beside the avatars. `flexWrap` with a basis on the text is the
            whole fix: where the two do not fit the sentence takes the next line at
            full width, and where they do it stays where it is. The text block is a
            column because the richest rung is two lines (*ten prize winners…* and
            the reason to go after them), and a `<Text>` cannot hold a `<Text>` with
            its own size.
          */}
          <View style={[a.flex_row, a.flex_wrap, a.align_center, a.gap_sm]}>
            <ModulePeerStack
              testID={testID ? `${testID}-faces` : undefined}
              faces={shownFaces}
              others={hiddenFaces}
              /*
                The rings are cut from the surface the row is drawn on — handed
                down by the caller that knows it, and the page only when nobody
                said (see `surfaceColor`).
              */
              ringColor={surfaceColor ?? t.atoms.bg.backgroundColor}
              /* The row *is* the way to the board, when there is a board. */
              onPress={openBoard}
              accessibilityLabel={facesLabel}
              accessibilityHint={
                openBoard
                  ? l`Opens the class's scores without playing the module`
                  : undefined
              }
            />
            <View
              testID={testID ? `${testID}-sentence` : undefined}
              /*
                The sheet is a list, not a clause beside the faces: full width.

                The first player's sentence is *not*. With one face on the row the
                column beside it is nearly the card's whole width, and the face in
                that row is the reader's own — so the two are read together, the
                copy in front of the photo rather than under it. It only ever fell
                to its own line because two sentences beside a single avatar looked
                squeezed; in front of the avatar, at the width the card actually
                has, they are four short lines and the row finally says *you*.
              */
              style={[
                styles.sentence,
                a.gap_xs,
                allCases ? {flexBasis: '100%'} : null,
              ]}>
              {/*
                The sheet, or the sentence.

                The chip's own switch: in test mode the one case that is true is
                replaced by every case of the reel, each read off a board invented
                for it (`moduleCaseFacts`) — see the header. Below this block the
                real card is left exactly as it is, because the sheet is a *spec* of
                the card rather than a second card.
              */}
              {allCases ? (
                <CaseDeck testID={testID} />
              ) : (
                <ClassSentenceText facts={audience} />
              )}
            </View>
          </View>

          {/*
            How far the class is from unlocking the top three.

            Its own component because the sheet prints one per case, off each case's
            invented board — and the rules it follows (the gate compared with `<`,
            the count that stops at the gate) are written there, once.
          */}
          <ClassCountdown facts={audience} />
        </View>
      }

      {progress && progress.done > 0 ? (
        <Text
          testID={testID ? `${testID}-progress` : undefined}
          style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {progressSentence(progress)}
        </Text>
      ) : null}

      {/*
        The one button, in one of two temperatures.

        Blue and quiet while there is room to drift; hot — the gradient, the fire,
        the one raised voice on the card — once the run is close enough that the
        only honest word for it is *finish it*. The gradient is painted *over* the
        button's own fill (see `GradientFill`), and the button clips it to its own
        pill.
      */}
      <Button
        testID={testID ? `${testID}-play` : undefined}
        label={label}
        size="large"
        color="primary"
        style={hot ? [a.overflow_hidden] : undefined}
        onPress={onPlay}>
        {hot ? (
          <GradientFill gradient={tokens.gradients.summer} />
        ) : (
          <ButtonIcon icon={Play} />
        )}
        <ButtonText>{label}</ButtonText>
      </Button>

      {audience.isPeer && audience.viewer ? (
        <OwnResult
          viewer={audience.viewer}
          testID={testID ? `${testID}-ownResult` : undefined}
          centered
        />
      ) : null}
    </View>
  )
}

const styles = {
  /*
   * The dev chip: the preview bar's own dashed red outline, at the size a card in
   * a feed can carry — dashed and red so it cannot be mistaken for app furniture.
   */
  cases: {
    alignSelf: 'flex-start',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(200, 60, 60, 0.5)',
    borderStyle: 'dashed',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  /*
   * The preview's ink, worn by everything the sheet writes in its own voice: the
   * chip's label and the `BUTTON —` line that closes every row of the deck.
   */
  previewInk: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.5,
    color: 'rgba(200, 60, 60, 0.9)',
  },
  casesPressed: {
    opacity: 0.7,
  },
  /*
   * One row of the sheet: what divides a case from the case under it.
   *
   * A hairline along the top only, in the preview's own dashed vocabulary's quieter
   * cousin — the colour comes from the theme (`border_contrast_low`) at the call
   * site, so a row reads as a row in both. A sheet is a table, and the first build's
   * rows ran into each other: thirteen blocks of four lines with nothing between
   * them but a slightly bolder first line.
   */
  row: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: 8,
  },
  sentence: {
    flexBasis: 168,
    flexGrow: 1,
    flexShrink: 1,
  },
} as const
